import { FC } from 'react';
import { Controller, FieldError, get, useFormContext } from 'react-hook-form';

import {
  Box,
  Checkbox,
  CheckboxProps,
  FormControlLabel,
  FormHelperText,
  InputLabel,
  styled,
  Tooltip,
} from '@mui/material';

import { FormElementProps } from '@/components';

export interface FormCheckboxFieldProps
  extends Omit<CheckboxProps, 'name' | 'onChange'>,
    FormElementProps {
  label?: string;
  tooltip?: string;
  handleChange?: (name: string, value: boolean) => void;
}

const Root = styled(Box)(({ theme }) => ({
  flex: 1,
  minWidth: '100%',
  maxWidth: '100%',
  marginBottom: 8,
  '& .MuiInputBase-root': {
    width: '100%',
  },
  '& .checkbox': { paddingTop: 0, paddingBottom: 0, marginTop: '20px' },
  '& .MuiFormControlLabel-label': {
    fontSize: '0.875rem',
    lineHeight: '1.5rem',
    color: theme.palette.text.primary,
    marginTop: '23px',
  },
}));

export const FormCheckboxField: FC<FormCheckboxFieldProps> = (props) => {
  const { label, name, rules, tooltip, handleChange, ...rest } = props;
  const {
    control,
    formState: { errors },
  } = useFormContext();
  const error = get(errors, name) as FieldError;

  const labelComponent = <InputLabel>{label}</InputLabel>;

  return (
    <Root>
      <Controller
        name={name}
        control={control}
        rules={rules}
        render={({ field }) => {
          return (
            <FormControlLabel
              control={
                <Checkbox
                  className="checkbox"
                  color="error"
                  {...rest}
                  name={field.name}
                  checked={Boolean(field.value)}
                  onChange={(e) => {
                    field.onChange(e);
                    if (handleChange) {
                      handleChange(name, e.target.checked);
                    }
                  }}
                />
              }
              label={
                tooltip ? (
                  <Tooltip title={tooltip} arrow placement="top">
                    {labelComponent}
                  </Tooltip>
                ) : (
                  labelComponent
                )
              }
            />
          );
        }}
      />
      <FormHelperText error>{error?.message}</FormHelperText>
    </Root>
  );
};
